/**
 * SAMURAI TCG - AJAX Cart Module
 * Handles cart operations without page reload using Shopify Cart AJAX API
 * + Bundled Section Rendering for cart drawer updates
 */

(function () {
  'use strict';

  const TCGCart = {
    /**
     * Get current cart state
     */
    async getCart() {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      return response.json();
    },

    /**
     * Add item(s) to cart
     * @param {number} variantId - Shopify variant ID
     * @param {number} quantity - Quantity to add
     * @param {Object} properties - Optional line item properties
     * @returns {Promise<Object>} Cart data
     */
    async addItem(variantId, quantity = 1, properties = {}) {
      const body = {
        items: [
          {
            id: variantId,
            quantity: quantity,
          },
        ],
        sections: 'tcg-cart-drawer',
        sections_url: window.location.pathname,
      };

      if (Object.keys(properties).length > 0) {
        body.items[0].properties = properties;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add item to cart');
      }

      const data = await response.json();

      // Update cart drawer with section rendering response
      if (data.sections && data.sections['tcg-cart-drawer']) {
        this.updateDrawerHTML(data.sections['tcg-cart-drawer']);
      }

      // Update header cart count/total
      await this.updateHeaderCart();

      return data;
    },

    /**
     * Add multiple items to cart at once (bulk add)
     * @param {Array<{id: number, quantity: number}>} items
     */
    async addItems(items) {
      const body = {
        items: items,
        sections: 'tcg-cart-drawer',
        sections_url: window.location.pathname,
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add items to cart');
      }

      const data = await response.json();

      if (data.sections && data.sections['tcg-cart-drawer']) {
        this.updateDrawerHTML(data.sections['tcg-cart-drawer']);
      }

      await this.updateHeaderCart();
      return data;
    },

    /**
     * Update item quantity in cart
     * @param {string} key - Line item key
     * @param {number} quantity - New quantity (0 to remove)
     */
    async updateItem(key, quantity) {
      const body = {
        id: key,
        quantity: quantity,
        sections: 'tcg-cart-drawer',
        sections_url: window.location.pathname,
      };

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const data = await response.json();

      if (data.sections && data.sections['tcg-cart-drawer']) {
        this.updateDrawerHTML(data.sections['tcg-cart-drawer']);
      }

      this.updateHeaderFromCart(data);
      return data;
    },

    /**
     * Remove item from cart
     */
    async removeItem(key) {
      return this.updateItem(key, 0);
    },

    /**
     * Update header cart count and total from cart data
     */
    updateHeaderFromCart(cart) {
      const countEl = document.getElementById('tcg-header-cart-count');
      const totalEl = document.getElementById('tcg-header-cart-total');

      if (countEl) countEl.textContent = cart.item_count;
      if (totalEl) {
        totalEl.textContent = this.formatMoney(cart.total_price);
      }
    },

    /**
     * Fetch cart and update header
     */
    async updateHeaderCart() {
      const cart = await this.getCart();
      this.updateHeaderFromCart(cart);
    },

    /**
     * Update cart drawer HTML from section rendering response
     */
    updateDrawerHTML(html) {
      const drawerBody = document.getElementById('tcg-cart-drawer-body');
      if (!drawerBody) return;

      // Parse the section rendering response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('.tcg-cart-drawer__body');
      const newFooter = doc.querySelector('.tcg-cart-drawer__footer');
      const newTitle = doc.querySelector('.tcg-cart-drawer__title');
      const newShippingBar = doc.querySelector('[data-tcg-shipping-bar]');

      if (newContent) {
        drawerBody.innerHTML = newContent.innerHTML;
      }

      if (newFooter) {
        const footer = document.querySelector('.tcg-cart-drawer__footer');
        if (footer) footer.innerHTML = newFooter.innerHTML;
      }

      if (newTitle) {
        const title = document.querySelector('.tcg-cart-drawer__title');
        if (title) title.innerHTML = newTitle.innerHTML;
      }

      // The bar only exists while free shipping is enabled (threshold > 0), so
      // reconcile presence too — otherwise a disabled offer leaves stale markup.
      const shippingBar = document.querySelector('[data-tcg-shipping-bar]');
      if (newShippingBar && shippingBar) {
        shippingBar.innerHTML = newShippingBar.innerHTML;
      } else if (newShippingBar) {
        drawerBody.parentNode.insertBefore(document.importNode(newShippingBar, true), drawerBody);
      } else if (shippingBar) {
        shippingBar.remove();
      }
    },

    /**
     * Format money in store's currency format
     * @param {number} cents - Amount in cents
     */
    formatMoney(cents) {
      const amount = (cents / 100).toLocaleString('en-US');
      return window.Shopify && window.Shopify.currency
        ? `${window.Shopify.currency.active} ${amount}`
        : `$${amount}`;
    },

    /**
     * Toggle cart drawer open/close
     */
    toggleDrawer() {
      const overlay = document.getElementById('tcg-cart-overlay');
      const drawer = document.getElementById('tcg-cart-drawer');

      if (overlay && drawer) {
        overlay.classList.toggle('is-open');
        drawer.classList.toggle('is-open');
        document.body.style.overflow = drawer.classList.contains('is-open')
          ? 'hidden'
          : '';
      }
    },

    /**
     * Open cart drawer
     */
    openDrawer() {
      const overlay = document.getElementById('tcg-cart-overlay');
      const drawer = document.getElementById('tcg-cart-drawer');

      if (overlay && drawer) {
        overlay.classList.add('is-open');
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    },

    /**
     * Close cart drawer
     */
    closeDrawer() {
      const overlay = document.getElementById('tcg-cart-overlay');
      const drawer = document.getElementById('tcg-cart-drawer');

      if (overlay && drawer) {
        overlay.classList.remove('is-open');
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    },
  };

  // Expose globally
  window.TCGCart = TCGCart;

  // === Event Delegation ===
  document.addEventListener('DOMContentLoaded', () => {
    // Cart toggle button in header
    document.addEventListener('click', (e) => {
      const cartToggle = e.target.closest('[data-tcg-cart-toggle]');
      if (cartToggle) {
        e.preventDefault();
        TCGCart.toggleDrawer();
      }

      // Cart overlay close
      if (e.target.id === 'tcg-cart-overlay') {
        TCGCart.closeDrawer();
      }

      // Cart drawer close button
      const closeBtn = e.target.closest('[data-tcg-cart-close]');
      if (closeBtn) {
        TCGCart.closeDrawer();
      }

      // Add to cart buttons
      const addBtn = e.target.closest('[data-tcg-add-to-cart]');
      if (addBtn) {
        e.preventDefault();
        handleAddToCart(addBtn);
      }

      // Quantity buttons
      const qtyBtn = e.target.closest('[data-tcg-qty]');
      if (qtyBtn) {
        handleQtyChange(qtyBtn);
      }

      // Remove from cart
      const removeBtn = e.target.closest('[data-tcg-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.tcgCartRemove;
        TCGCart.removeItem(key);
      }

      // Cart item quantity update
      const cartQtyBtn = e.target.closest('[data-tcg-cart-qty]');
      if (cartQtyBtn) {
        const key = cartQtyBtn.dataset.tcgCartKey;
        const delta = parseInt(cartQtyBtn.dataset.tcgCartQty);
        const input = cartQtyBtn
          .closest('.tcg-cart-drawer__item-qty')
          ?.querySelector('input');
        if (input && key) {
          const newQty = Math.max(0, parseInt(input.value || 1) + delta);
          TCGCart.updateItem(key, newQty);
        }
      }
    });

    // Keyboard: Escape to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        TCGCart.closeDrawer();
      }
    });
  });

  /**
   * Handle add-to-cart button click
   */
  async function handleAddToCart(btn) {
    if (btn.disabled || btn.classList.contains('is-loading')) return;

    const variantId = btn.dataset.tcgAddToCart;
    const card = btn.closest('.tcg-card');
    const qtyInput = card
      ? card.querySelector('.tcg-qty-input')
      : btn.parentElement.querySelector('.tcg-qty-input');
    const qty = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

    // 先行クリックのリセットタイマーが残っていると、後続のスピナーを潰してしまう
    if (btn._tcgResetTimer) {
      clearTimeout(btn._tcgResetTimer);
      btn._tcgResetTimer = null;
      btn.classList.remove('is-added');
      btn.innerHTML = btn.dataset.tcgLabel || btn.innerHTML;
    }

    // Show loading state
    const originalHTML = btn.innerHTML;
    btn.classList.add('is-loading');
    btn.innerHTML = '<span class="tcg-spinner"></span>';

    try {
      await TCGCart.addItem(parseInt(variantId), qty);

      // Success feedback
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.innerHTML = '✓ Added';

      // Show toast
      // 商品ページには .tcg-card が無いので h1 から拾う (カード名が取れないと
      // 「… added to cart」だけのトーストになる)
      const productName = (
        card
          ? card.querySelector('.tcg-card__name')?.textContent
          : document.querySelector('.tcg-pdp__title, h1')?.textContent
      ) || '';
      const label = productName.trim().slice(0, 25);
      TCGToast.show(label ? `${label}… added to cart` : 'Added to cart');

      // Reset button after delay
      // data-tcg-label は商品ページのバリアント切替が更新する。非同期処理中に
      // 別バリアントへ切り替わった場合、originalHTML を戻すと disabled のまま
      // 「Add to Cart」を表示してしまうため、控えがあればそちらを優先する
      btn._tcgResetTimer = setTimeout(() => {
        btn._tcgResetTimer = null;
        btn.classList.remove('is-added');
        btn.innerHTML = btn.dataset.tcgLabel || originalHTML;
      }, 1200);
    } catch (error) {
      btn.classList.remove('is-loading');
      btn.innerHTML = btn.dataset.tcgLabel || originalHTML;
      TCGToast.show(error.message, 'error');
    }
  }

  /**
   * Handle quantity +/- buttons on product cards
   */
  function handleQtyChange(btn) {
    const delta = parseInt(btn.dataset.tcgQty);
    const group = btn.closest('.tcg-qty-group');
    if (!group) return;

    const input = group.querySelector('.tcg-qty-input');
    if (!input) return;

    let val = parseInt(input.value) || 1;
    val = Math.max(1, Math.min(99, val + delta));
    input.value = val;
  }
})();
